import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, IconButton, TextField } from '@mui/material';
import PoolIcon from '@mui/icons-material/Pool';
import {
  DragIndicator,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Lock,
} from '@mui/icons-material';
import { PiletaDevice } from '../../services/piletaApi';
import { useAiDevicePolicy, useShowAiCardBadge } from '../../contexts/AiDevicePolicyContext';
import DeviceTypeImage from '../shared/DeviceTypeImage';
import { DeviceIconWithBrandChip } from '../shared/DeviceBrandChip';
import { artifactTypeLineSx, sonosCardOuterSx } from '../../installed/music';

const ACCENT = '#2391FF';
const CARD_BG = '#2F3235';
const CHIP_TINT = '#4FC3F7';
/** Borde + título Pileta:/Bomba: — mismos para ambos chips. */
const CHIP_CHROME_ACTIVE = '#81C784';
const CHIP_CHROME_IDLE = 'rgba(255,255,255,0.28)';
const CHIP_TITLE_IDLE = 'rgba(255,255,255,0.55)';
const IDLE_FUNC_COLOR = '#90A4AE';
const TITLE_SEP_COLOR = 'rgba(255,255,255,0.45)';

/** Colores vívidos/contrastantes por función. */
const FUNC_COLORS: Record<string, string> = {
  filtrar: '#00E5FF',
  limpiar: '#D500F9',       /* violeta intenso (antes se veía apagado) */
  vaciar: '#B388FF',        /* lila, distinto del naranja de calentar */
  calentar: '#FF6D00',      /* naranja intenso (no rojo) */
  luces: '#FFEA00',
  canilla: '#40C4FF',       /* cyan-azul brillante */
  tanque: '#FFD740',        /* ámbar, no verde (evitar confusión con riego) */
  riego: '#76FF03',         /* verde lima */
  pileta: '#29B6F6',        /* celeste más vivo */
};

/**
 * Columna derecha (hueco del ON/OFF).
 * Ancho pensado para 1–2 funciones; con 2 funcs se acorta el prefijo a inicial.
 */
const CHIPS_COLUMN_WIDTH = 128;

const STATUS_CHIP_SX = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.35,
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  px: 0.7,
  py: 0.35,
  borderRadius: 999,
  height: 24,
  boxSizing: 'border-box' as const,
} as const;

const FUNC_LABELS: Record<string, string> = {
  filtrar: 'Filtrar',
  limpiar: 'Limpiar',
  vaciar: 'Vaciar',
  calentar: 'Calentar',
  luces: 'Luces',
  canilla: 'Canilla',
  tanque: 'Tanque',
  riego: 'Riego',
  pileta: 'Pileta',
};

interface Props {
  device: PiletaDevice;
  onOpen?: (device: PiletaDevice) => void;
  interactionLocked?: boolean;
  editMode?: boolean;
  isRoomEditing?: boolean;
  isEditingName?: boolean;
  dragHandleProps?: Record<string, unknown>;
  onStartEditName?: () => void;
  onSaveEditName?: (newName: string) => void;
  onCancelEditName?: () => void;
  showBrandChip?: boolean;
  /** Fuerza re-render del badge IA cuando cambia la política (memo). */
  aiPolicyRevision?: number;
}

function resolveActiveKeys(
  active: string[] | undefined,
  labels: string[] | undefined,
): string[] {
  if (active && active.length > 0) return active;
  if (!labels || labels.length === 0) return [];
  const byLabel: Record<string, string> = {
    Filtrar: 'filtrar',
    Limpiar: 'limpiar',
    Vaciar: 'vaciar',
    Calentar: 'calentar',
    Luces: 'luces',
    Canilla: 'canilla',
    Tanque: 'tanque',
    Riego: 'riego',
    Pileta: 'pileta',
  };
  return labels.map((l) => byLabel[l] || l.toLowerCase());
}

const PiletaDeviceCard: React.FC<Props> = ({
  device,
  onOpen,
  interactionLocked = false,
  editMode = false,
  isRoomEditing = false,
  isEditingName = false,
  dragHandleProps,
  onStartEditName,
  onSaveEditName,
  onCancelEditName,
  showBrandChip = false,
  aiPolicyRevision = 0,
}) => {
  const { t } = useTranslation();
  const showAiCardBadge = useShowAiCardBadge();
  const aiPolicy = useAiDevicePolicy();
  const aiWhitelisted = showAiCardBadge && aiPolicy.isPiletaWhitelistedForIcon(device.id);
  const aiBlacklisted = showAiCardBadge && aiPolicy.isPiletaBlacklistedForIcon(device.id);
  const locked = interactionLocked || isRoomEditing || isEditingName;
  const offline = device.online === false;
  const pinRequired = Boolean(device.pin_required);
  const hasArtefactoIcon = Boolean(device.artefacto?.nombre || device.artefacto_id);
  const artefactoNombre = device.artefacto?.nombre || null;
  const tipoArtefacto = artefactoNombre
    ?? t('pileta.card.fallbackType', { defaultValue: 'Pileta' });
  const title = device.nombre || device.display_name || t('pileta.card.fallbackType', { defaultValue: 'Pileta' });
  const [editingNameValue, setEditingNameValue] = useState(title);

  useEffect(() => {
    if (isEditingName) setEditingNameValue(title);
  }, [isEditingName, device.id, title]);

  const chips = device.chips_state;
  const piletaActive = resolveActiveKeys(chips?.pileta?.active, chips?.pileta?.labels);
  const bombaActive = resolveActiveKeys(chips?.bomba?.active, chips?.bomba?.labels);
  const piletaHasActive = piletaActive.length > 0;
  const bombaHasActive = bombaActive.length > 0;

  const showBadgesRow = Boolean(artefactoNombre) || offline || pinRequired || aiWhitelisted || aiBlacklisted;
  void aiPolicyRevision;

  const renderFuncSpans = (keys: string[]) => {
    if (keys.length === 0) {
      return (
        <Box component="span" sx={{ color: IDLE_FUNC_COLOR, fontWeight: 600 }}>
          —
        </Box>
      );
    }
    return keys.map((key, idx) => {
      const color = FUNC_COLORS[key] || ACCENT;
      const pretty = FUNC_LABELS[key] || (key.charAt(0).toUpperCase() + key.slice(1));
      return (
        <React.Fragment key={key}>
          {idx > 0 ? (
            <Box component="span" sx={{ color: TITLE_SEP_COLOR, fontWeight: 700 }}> · </Box>
          ) : null}
          <Box component="span" sx={{ color, fontWeight: 800 }}>{pretty}</Box>
        </React.Fragment>
      );
    });
  };

  const renderStatusChip = (
    titleKey: string,
    titleDefault: string,
    shortDefault: string,
    activeKeys: string[],
    hasActive: boolean,
  ) => {
    /* Con 2+ funciones concurrentes el prefijo completo no entra: solo inicial. */
    const useShortPrefix = activeKeys.length >= 2;
    const prefix = useShortPrefix
      ? t(`${titleKey}Short`, { defaultValue: shortDefault })
      : t(titleKey, { defaultValue: titleDefault });

    return (
      <Box sx={{
        ...STATUS_CHIP_SX,
        bgcolor: 'transparent',
        border: `1.5px solid ${hasActive ? CHIP_CHROME_ACTIVE : CHIP_CHROME_IDLE}`,
        boxShadow: 'none',
      }}>
        <Typography
          component="div"
          sx={{
            fontSize: 10.5,
            fontWeight: 600,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            minWidth: 0,
            flex: 1,
            lineHeight: 1.2,
            display: 'flex',
            alignItems: 'center',
            gap: 0.25,
          }}
        >
          <Box
            component="span"
            sx={{
              color: hasActive ? CHIP_CHROME_ACTIVE : CHIP_TITLE_IDLE,
              flexShrink: 0,
              fontWeight: 700,
            }}
          >
            {prefix}
          </Box>
          <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {renderFuncSpans(activeKeys)}
          </Box>
        </Typography>
        <Box
          sx={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            flexShrink: 0,
            ml: 0.2,
            bgcolor: hasActive ? '#69F0AE' : '#78909C',
            boxShadow: hasActive ? '0 0 8px #69F0AE' : 'none',
            opacity: hasActive ? 1 : 0.65,
          }}
        />
      </Box>
    );
  };

  return (
    <Box
      sx={{
        ...sonosCardOuterSx,
        backgroundColor: CARD_BG,
        opacity: 1,
        overflow: 'visible',
        pointerEvents: 'none',
        '& button, & .MuiIconButton-root, & .MuiButton-root, & [role="button"], & .MuiCheckbox-root, & .MuiInput-root, & [data-device-icon]': {
          pointerEvents: 'auto',
          touchAction: isRoomEditing ? 'none' : 'manipulation',
        },
      }}
    >
      {/* En modo edición Settings el overlay captura el mini-lápiz: no montarlo. */}
      {!isRoomEditing && !isEditingName && !editMode && (
        <Box
          onClick={(e) => {
            if (locked) return;
            const target = e.target as HTMLElement;
            if (
              target.closest('button')
              || target.closest('.MuiIconButton-root')
              || target.closest('.MuiCheckbox-root')
              || target.closest('[data-device-icon]')
            ) {
              return;
            }
            const scrolled = (window as any).dashboardHasScrolledRef?.current;
            if (!scrolled) onOpen?.(device);
          }}
          sx={{
            position: 'absolute',
            top: 0,
            left: '80px',
            right: 0,
            bottom: 0,
            pointerEvents: 'auto',
            touchAction: 'pan-y',
            zIndex: 1,
            cursor: 'pointer',
          }}
        />
      )}

      <DeviceIconWithBrandChip
        brand="pileta"
        showBrandChip={showBrandChip}
        sx={{ marginRight: 1 }}
      >
        <Box data-device-icon sx={{ flexShrink: 0, touchAction: 'pan-y', pointerEvents: 'auto' }}>
          {hasArtefactoIcon ? (
            <DeviceTypeImage
              tipo="Pileta"
              tipo_artefacto={tipoArtefacto}
              iconOverride={device.artefacto?.icono ?? null}
              colorOverride={device.artefacto?.color_icono ?? ACCENT}
            />
          ) : (
            <Box sx={{
              width: 60, height: 60, borderRadius: '50%', bgcolor: '#1B1D21',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <PoolIcon sx={{ fontSize: 28, color: CHIP_TINT, opacity: 0.7 }} />
            </Box>
          )}
        </Box>
      </DeviceIconWithBrandChip>

      <Box sx={{
        flex: 1,
        minWidth: 0,
        overflow: 'hidden',
        transform: 'translateY(-2px)',
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: 0.1,
          position: 'relative',
          zIndex: isEditingName || editMode ? 100 : 'auto',
        }}>
          {isEditingName ? (
            <>
              <TextField
                value={editingNameValue}
                onChange={(e) => setEditingNameValue(e.target.value)}
                variant="standard"
                autoFocus
                onClick={(e) => e.stopPropagation()}
                sx={{
                  flex: 1,
                  '& .MuiInput-root': { color: 'white', fontSize: 14, fontWeight: 'bold' },
                  '& .MuiInput-underline:before': { borderBottomColor: '#666' },
                  '& .MuiInput-underline:after': { borderBottomColor: '#2391FF' },
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') onSaveEditName?.(editingNameValue);
                }}
              />
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); onSaveEditName?.(editingNameValue); }} sx={{ padding: 1, marginLeft: 0.5, minWidth: 32, minHeight: 32, zIndex: 101, backgroundColor: 'rgba(35, 145, 255, 0.1)' }}>
                <CheckIcon sx={{ fontSize: 18, color: '#2391FF' }} />
              </IconButton>
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); onCancelEditName?.(); }} sx={{ padding: 1, marginLeft: 0.5, minWidth: 32, minHeight: 32, zIndex: 101, backgroundColor: 'rgba(255, 68, 68, 0.1)' }}>
                <CloseIcon sx={{ fontSize: 18, color: '#ff4444' }} />
              </IconButton>
            </>
          ) : (
            <>
              <Typography sx={{ fontSize: 14, fontWeight: 'bold', color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                {title}
              </Typography>
              {editMode && !isRoomEditing && onStartEditName && (
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); onStartEditName(); }} sx={{ padding: 0.25, marginLeft: 0.5, flexShrink: 0, position: 'relative', zIndex: 4, '&:hover': { backgroundColor: 'rgba(35, 145, 255, 0.1)' } }}>
                  <EditIcon sx={{ fontSize: 14, color: '#2391FF' }} />
                </IconButton>
              )}
            </>
          )}
        </Box>

        {/* Tipo de artefacto + badges (offline / PIN / IA) a la derecha, sin pelear con chips */}
        {showBadgesRow ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              minWidth: 0,
              marginTop: 0.25,
            }}
          >
            {artefactoNombre ? (
              <Typography
                sx={{
                  ...artifactTypeLineSx,
                  marginTop: 0,
                  flex: '1 1 auto',
                  minWidth: 0,
                }}
              >
                {artefactoNombre}
              </Typography>
            ) : (
              <Box sx={{ flex: '1 1 auto', minWidth: 0 }} />
            )}
            {(offline || pinRequired || aiWhitelisted || aiBlacklisted) && !isRoomEditing && (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  flexShrink: 0,
                  pointerEvents: 'none',
                }}
              >
                {pinRequired && (
                  <Lock sx={{ fontSize: 13, color: '#2391FF' }} />
                )}
                {aiWhitelisted && (
                  <Typography
                    component="span"
                    aria-label={t('deviceList.aiPolicyAllowed') as string}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.62rem',
                      fontWeight: 800,
                      color: '#66bb6a',
                      minHeight: 14,
                      lineHeight: '14px',
                      letterSpacing: '-0.02em',
                      userSelect: 'none',
                    }}
                  >
                    {t('common.aiBadge')}
                  </Typography>
                )}
                {aiBlacklisted && (
                  <Typography
                    component="span"
                    aria-label={t('deviceList.aiPolicyDenied') as string}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.62rem',
                      fontWeight: 800,
                      color: '#ef5350',
                      minHeight: 14,
                      lineHeight: '14px',
                      letterSpacing: '-0.02em',
                      userSelect: 'none',
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        textDecoration: 'line-through',
                        textDecorationColor: '#ef5350',
                        textDecorationThickness: '1.5px',
                      }}
                    >
                      {t('common.aiBadge')}
                    </Box>
                  </Typography>
                )}
                {offline && (
                  <Typography sx={{
                    fontSize: 10,
                    fontWeight: 'bold',
                    padding: '0px 4.4px',
                    borderRadius: '8.2px',
                    textAlign: 'center',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    whiteSpace: 'nowrap',
                    lineHeight: 1.0,
                    color: '#ff4444',
                    backgroundColor: 'rgba(255, 68, 68, 0.1)',
                    border: '1px solid #ff4444',
                  }}>
                    {t('devices.offline')}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        ) : null}
      </Box>

      {/* Chips a la derecha (hueco del ON/OFF); ocultos en reordenar para no pelear con el drag handle */}
      {!isRoomEditing && (
        <Box
          sx={{
            flexShrink: 0,
            width: CHIPS_COLUMN_WIDTH,
            ml: 0.5,
            mr: 0.25,
            alignSelf: 'stretch',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'stretch',
            gap: 0.45,
            position: 'relative',
            zIndex: 0,
            pointerEvents: 'none',
            '& *': { pointerEvents: 'none' },
          }}
        >
          {renderStatusChip(
            'pileta.card.chipPileta',
            'Pileta:',
            'P:',
            piletaActive,
            piletaHasActive,
          )}
          {renderStatusChip(
            'pileta.card.chipBomba',
            'Bomba:',
            'B:',
            bombaActive,
            bombaHasActive,
          )}
        </Box>
      )}

      {isRoomEditing && (
        <Box
          {...dragHandleProps}
          sx={{
            marginLeft: 0.5,
            transform: 'translateX(-10px)',
            flexShrink: 0,
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '40px',
            height: '40px',
            cursor: 'grab',
            pointerEvents: 'auto',
            touchAction: 'none',
            '&:active': { cursor: 'grabbing' },
          }}
        >
          <DragIndicator sx={{ color: 'white', fontSize: '2.25rem', pointerEvents: 'none' }} />
        </Box>
      )}
    </Box>
  );
};

export default React.memo(PiletaDeviceCard, (prev, next) => (
  prev.device.id === next.device.id
  && prev.device.nombre === next.device.nombre
  && prev.device.display_name === next.device.display_name
  && prev.device.online === next.device.online
  && prev.device.artefacto_id === next.device.artefacto_id
  && prev.device.artefacto?.color_icono === next.device.artefacto?.color_icono
  && prev.device.artefacto?.nombre === next.device.artefacto?.nombre
  && prev.device.pin_required === next.device.pin_required
  && prev.device.chips_state?.pileta?.text === next.device.chips_state?.pileta?.text
  && prev.device.chips_state?.bomba?.text === next.device.chips_state?.bomba?.text
  && JSON.stringify(prev.device.chips_state?.pileta?.active) === JSON.stringify(next.device.chips_state?.pileta?.active)
  && JSON.stringify(prev.device.chips_state?.bomba?.active) === JSON.stringify(next.device.chips_state?.bomba?.active)
  && JSON.stringify(prev.device.chips_state?.pileta?.labels) === JSON.stringify(next.device.chips_state?.pileta?.labels)
  && JSON.stringify(prev.device.chips_state?.bomba?.labels) === JSON.stringify(next.device.chips_state?.bomba?.labels)
  && prev.interactionLocked === next.interactionLocked
  && prev.editMode === next.editMode
  && prev.isRoomEditing === next.isRoomEditing
  && prev.isEditingName === next.isEditingName
  && prev.showBrandChip === next.showBrandChip
  && prev.aiPolicyRevision === next.aiPolicyRevision
  && prev.onOpen === next.onOpen
));
